import React, { useState } from 'react';
import '../../assests/css/admin/Settings.css';

const Setting = () => {
  const [activeTab, setActiveTab] = useState('store-profile');
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({});
  const [showImageGallery, setShowImageGallery] = useState(false);

  // Store Profile State
  const [storeProfile, setStoreProfile] = useState({
    logo: 'https://via.placeholder.com/150?text=STORE',
    storeName: 'Modern Living Essentials',
    legalName: 'MLE Retail Group LLC',
    category: 'Home & Garden',
    supportPhone: '+1 (555) 000-1234',
    supportEmail: 'support@modernliving.com'
  });

  // Notification Preferences State
  const [notifications, setNotifications] = useState({
    orderNotifications: true,
    inventoryAlerts: true,
    smsAlerts: false
  });

  // Payment Methods State
  const [paymentMethods, setPaymentMethods] = useState({
    creditCard: true,
    debitCard: true,
    paypal: false,
    stripe: true,
    cashOnDelivery: true
  });

  // Shipping & Delivery State
  const [shipping, setShipping] = useState({
    freeShippingThreshold: '50',
    standardShippingCost: '5.99',
    expressShippingCost: '12.99',
    internationalShipping: false,
    estimatedDelivery: '3-5 business days'
  });

  // Tax Settings State
  const [taxSettings, setTaxSettings] = useState({
    enableTax: true,
    taxRate: '8.5',
    taxInclusive: false,
    taxRegistrationNumber: 'TX123456789'
  });

  // Security & Roles State
  const [security, setSecuritySettings] = useState({
    twoFactorAuth: true,
    sessionTimeout: '30',
    passwordExpiry: '90',
    loginAttempts: '5'
  });

  // Languages State
  const [languages, setLanguages] = useState({
    defaultLanguage: 'English',
    supportedLanguages: ['English', 'Spanish', 'French'],
    rtlSupport: false
  });

  // Sample images for gallery
  const galleryImages = [
    'https://via.placeholder.com/150?text=Logo+1',
    'https://via.placeholder.com/150?text=Logo+2',
    'https://via.placeholder.com/150?text=Logo+3',
    'https://via.placeholder.com/150?text=Logo+4',
    'https://via.placeholder.com/150?text=Logo+5',
    'https://via.placeholder.com/150?text=Logo+6'
  ];

  // SweetAlert-like custom alert
  const showSweetAlert = (type, title, message, onConfirm) => {
    setAlertConfig({
      type,
      title,
      message,
      onConfirm
    });
    setShowAlert(true);
  };

  const handleSaveEdit = (section, updatedData) => {
    showSweetAlert(
      'edit',
      `Edit ${section}`,
      `Make your changes and click save.`,
      (formData) => {
        switch(section) {
          case 'Store Profile':
            setStoreProfile(prev => ({ ...prev, ...formData }));
            break;
          case 'Notifications':
            setNotifications(prev => ({ ...prev, ...formData }));
            break;
          case 'Payment Methods':
            setPaymentMethods(prev => ({ ...prev, ...formData }));
            break;
          case 'Shipping':
            setShipping(prev => ({ ...prev, ...formData }));
            break;
          case 'Tax Settings':
            setTaxSettings(prev => ({ ...prev, ...formData }));
            break;
          case 'Security':
            setSecuritySettings(prev => ({ ...prev, ...formData }));
            break;
          case 'Languages':
            setLanguages(prev => ({ ...prev, ...formData }));
            break;
        }
        showSweetAlert('success', 'Saved!', 'Your changes have been saved successfully.', null);
      }
    );
  };

  const handleImageSelect = (image) => {
    setStoreProfile(prev => ({ ...prev, logo: image }));
    setShowImageGallery(false);
    showSweetAlert('success', 'Logo Updated!', 'Your store logo has been updated successfully.', null);
  };

  const handleRemoveLogo = () => {
    showSweetAlert('warning', 'Remove Logo?', 'Are you sure you want to remove the store logo?', () => {
      setStoreProfile(prev => ({ ...prev, logo: 'https://via.placeholder.com/150?text=NO+LOGO' }));
      showSweetAlert('success', 'Removed!', 'Store logo has been removed.', null);
    });
  };

  // Render different tab contents
  const renderTabContent = () => {
    switch(activeTab) {
      case 'store-profile':
        return (
          <div className="tab-content">
            <div className="settings-card">
              <div className="card-header">
                <h3>Store Logo</h3>
              </div>
              <div className="logo-section">
                <div className="logo-preview">
                  <img src={storeProfile.logo} alt="Store Logo" />
                  <div className="logo-badge">✓</div>
                </div>
                <div className="logo-info">
                  <h4>Store Logo</h4>
                  <p>This logo will appear on your customer receipts, checkout pages, and notification emails.</p>
                  <p className="recommended">Recommend size: 512×512px.</p>
                  <div className="logo-actions">
                    <button className="btn btn-primary" onClick={() => setShowImageGallery(true)}>
                      Upload New
                    </button>
                    <button className="btn btn-secondary" onClick={handleRemoveLogo}>
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="settings-card">
              <div className="card-header">
                <h3>Basic Information</h3>
                <button className="btn-edit" onClick={() => handleSaveEdit('Store Profile', storeProfile)}>
                  ✏️ Edit
                </button>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Store Name</label>
                  <input type="text" value={storeProfile.storeName} readOnly />
                </div>
                <div className="form-group">
                  <label>Legal Business Name</label>
                  <input type="text" value={storeProfile.legalName} readOnly />
                </div>
                <div className="form-group">
                  <label>Store Category</label>
                  <input type="text" value={storeProfile.category} readOnly />
                </div>
                <div className="form-group">
                  <label>Support Phone</label>
                  <input type="text" value={storeProfile.supportPhone} readOnly />
                </div>
                <div className="form-group full-width">
                  <label>Support Email</label>
                  <input type="email" value={storeProfile.supportEmail} readOnly />
                  <small>Customers will use this address if they have questions about their orders.</small>
                </div>
              </div>
            </div>

            <div className="settings-card">
              <div className="card-header">
                <h3>Notification Preferences</h3>
                <button className="btn-edit" onClick={() => handleSaveEdit('Notifications', notifications)}>
                  ✏️ Edit
                </button>
              </div>
              <div className="notification-list">
                <div className="notification-item">
                  <div>
                    <h4>Order Notifications</h4>
                    <p>Receive an email every time a customer places a new order.</p>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" checked={notifications.orderNotifications} readOnly />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="notification-item">
                  <div>
                    <h4>Inventory Alerts</h4>
                    <p>Get notified when product stock levels fall below the minimum threshold.</p>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" checked={notifications.inventoryAlerts} readOnly />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="notification-item">
                  <div>
                    <h4>SMS Alerts</h4>
                    <p>Send critical updates directly to your mobile device.</p>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" checked={notifications.smsAlerts} readOnly />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      case 'payment-methods':
        return (
          <div className="tab-content">
            <div className="settings-card">
              <div className="card-header">
                <h3>Payment Methods</h3>
                <button className="btn-edit" onClick={() => handleSaveEdit('Payment Methods', paymentMethods)}>
                  ✏️ Edit
                </button>
              </div>
              <div className="payment-methods-grid">
                <div className="payment-method-item">
                  <div className="payment-icon">💳</div>
                  <div>
                    <h4>Credit Card</h4>
                    <p>Accept Visa, Mastercard, Amex</p>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" checked={paymentMethods.creditCard} readOnly />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="payment-method-item">
                  <div className="payment-icon">💳</div>
                  <div>
                    <h4>Debit Card</h4>
                    <p>Accept all major debit cards</p>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" checked={paymentMethods.debitCard} readOnly />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="payment-method-item">
                  <div className="payment-icon">🅿️</div>
                  <div>
                    <h4>PayPal</h4>
                    <p>Fast and secure PayPal payments</p>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" checked={paymentMethods.paypal} readOnly />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="payment-method-item">
                  <div className="payment-icon">💰</div>
                  <div>
                    <h4>Stripe</h4>
                    <p>Online payment processing</p>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" checked={paymentMethods.stripe} readOnly />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="payment-method-item">
                  <div className="payment-icon">💵</div>
                  <div>
                    <h4>Cash on Delivery</h4>
                    <p>Pay when order is delivered</p>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" checked={paymentMethods.cashOnDelivery} readOnly />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      case 'shipping':
        return (
          <div className="tab-content">
            <div className="settings-card">
              <div className="card-header">
                <h3>Shipping & Delivery</h3>
                <button className="btn-edit" onClick={() => handleSaveEdit('Shipping', shipping)}>
                  ✏️ Edit
                </button>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Free Shipping Threshold ($)</label>
                  <input type="text" value={shipping.freeShippingThreshold} readOnly />
                </div>
                <div className="form-group">
                  <label>Standard Shipping Cost ($)</label>
                  <input type="text" value={shipping.standardShippingCost} readOnly />
                </div>
                <div className="form-group">
                  <label>Express Shipping Cost ($)</label>
                  <input type="text" value={shipping.expressShippingCost} readOnly />
                </div>
                <div className="form-group">
                  <label>Estimated Delivery Time</label>
                  <input type="text" value={shipping.estimatedDelivery} readOnly />
                </div>
                <div className="form-group full-width">
                  <div className="notification-item">
                    <div>
                      <h4>International Shipping</h4>
                      <p>Enable shipping to international destinations</p>
                    </div>
                    <label className="toggle">
                      <input type="checkbox" checked={shipping.internationalShipping} readOnly />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'tax-settings':
        return (
          <div className="tab-content">
            <div className="settings-card">
              <div className="card-header">
                <h3>Tax Settings</h3>
                <button className="btn-edit" onClick={() => handleSaveEdit('Tax Settings', taxSettings)}>
                  ✏️ Edit
                </button>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Tax Rate (%)</label>
                  <input type="text" value={taxSettings.taxRate} readOnly />
                </div>
                <div className="form-group">
                  <label>Tax Registration Number</label>
                  <input type="text" value={taxSettings.taxRegistrationNumber} readOnly />
                </div>
                <div className="form-group full-width">
                  <div className="notification-item">
                    <div>
                      <h4>Enable Tax</h4>
                      <p>Apply tax to all orders</p>
                    </div>
                    <label className="toggle">
                      <input type="checkbox" checked={taxSettings.enableTax} readOnly />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
                <div className="form-group full-width">
                  <div className="notification-item">
                    <div>
                      <h4>Tax Inclusive Pricing</h4>
                      <p>Show prices with tax included</p>
                    </div>
                    <label className="toggle">
                      <input type="checkbox" checked={taxSettings.taxInclusive} readOnly />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="tab-content">
            <div className="settings-card">
              <div className="card-header">
                <h3>Security & Roles</h3>
                <button className="btn-edit" onClick={() => handleSaveEdit('Security', security)}>
                  ✏️ Edit
                </button>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Session Timeout (minutes)</label>
                  <input type="text" value={security.sessionTimeout} readOnly />
                </div>
                <div className="form-group">
                  <label>Password Expiry (days)</label>
                  <input type="text" value={security.passwordExpiry} readOnly />
                </div>
                <div className="form-group">
                  <label>Max Login Attempts</label>
                  <input type="text" value={security.loginAttempts} readOnly />
                </div>
                <div className="form-group full-width">
                  <div className="notification-item">
                    <div>
                      <h4>Two-Factor Authentication</h4>
                      <p>Add an extra layer of security to your account</p>
                    </div>
                    <label className="toggle">
                      <input type="checkbox" checked={security.twoFactorAuth} readOnly />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'languages':
        return (
          <div className="tab-content">
            <div className="settings-card">
              <div className="card-header">
                <h3>Languages</h3>
                <button className="btn-edit" onClick={() => handleSaveEdit('Languages', languages)}>
                  ✏️ Edit
                </button>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Default Language</label>
                  <input type="text" value={languages.defaultLanguage} readOnly />
                </div>
                <div className="form-group">
                  <label>Supported Languages</label>
                  <input type="text" value={languages.supportedLanguages.join(', ')} readOnly />
                </div>
                <div className="form-group full-width">
                  <div className="notification-item">
                    <div>
                      <h4>RTL Support</h4>
                      <p>Enable right-to-left text direction</p>
                    </div>
                    <label className="toggle">
                      <input type="checkbox" checked={languages.rtlSupport} readOnly />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="admin-store-settings">
      <div className="settings-header">
        <h1>Store Settings</h1>
        <p>Configure your store's public identity and internal business information.</p>
      </div>

      <div className="settings-nav">
        <button 
          className={activeTab === 'store-profile' ? 'active' : ''}
          onClick={() => setActiveTab('store-profile')}
        >
          🏪 Store Profile
        </button>
        <button 
          className={activeTab === 'payment-methods' ? 'active' : ''}
          onClick={() => setActiveTab('payment-methods')}
        >
          💳 Payment Methods
        </button>
        <button 
          className={activeTab === 'shipping' ? 'active' : ''}
          onClick={() => setActiveTab('shipping')}
        >
          🚚 Shipping & Delivery
        </button>
        <button 
          className={activeTab === 'tax-settings' ? 'active' : ''}
          onClick={() => setActiveTab('tax-settings')}
        >
          📋 Tax Settings
        </button>
        <button 
          className={activeTab === 'security' ? 'active' : ''}
          onClick={() => setActiveTab('security')}
        >
          🔒 Security & Roles
        </button>
        <button 
          className={activeTab === 'languages' ? 'active' : ''}
          onClick={() => setActiveTab('languages')}
        >
          🌐 Languages
        </button>
      </div>

      {renderTabContent()}

      {/* Custom SweetAlert-like Modal */}
      {showAlert && (
        <div className="sweet-alert-overlay" onClick={() => alertConfig.type !== 'edit' && setShowAlert(false)}>
          <div className="sweet-alert-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`alert-icon ${alertConfig.type}`}>
              {alertConfig.type === 'success' && '✓'}
              {alertConfig.type === 'warning' && '⚠'}
              {alertConfig.type === 'edit' && '✏️'}
            </div>
            <h2>{alertConfig.title}</h2>
            <p>{alertConfig.message}</p>
            
            {alertConfig.type === 'edit' && (
              <div className="edit-form">
                {activeTab === 'store-profile' && (
                  <>
                    <input 
                      type="text" 
                      placeholder="Store Name" 
                      defaultValue={storeProfile.storeName}
                      id="storeName"
                    />
                    <input 
                      type="text" 
                      placeholder="Legal Name" 
                      defaultValue={storeProfile.legalName}
                      id="legalName"
                    />
                    <input 
                      type="text" 
                      placeholder="Category" 
                      defaultValue={storeProfile.category}
                      id="category"
                    />
                    <input 
                      type="text" 
                      placeholder="Support Phone" 
                      defaultValue={storeProfile.supportPhone}
                      id="supportPhone"
                    />
                    <input 
                      type="email" 
                      placeholder="Support Email" 
                      defaultValue={storeProfile.supportEmail}
                      id="supportEmail"
                    />
                  </>
                )}
              </div>
            )}

            <div className="alert-actions">
              {alertConfig.type === 'edit' ? (
                <>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => setShowAlert(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary" 
                    onClick={() => {
                      if (alertConfig.onConfirm) {
                        const formData = {};
                        if (activeTab === 'store-profile') {
                          formData.storeName = document.getElementById('storeName')?.value || storeProfile.storeName;
                          formData.legalName = document.getElementById('legalName')?.value || storeProfile.legalName;
                          formData.category = document.getElementById('category')?.value || storeProfile.category;
                          formData.supportPhone = document.getElementById('supportPhone')?.value || storeProfile.supportPhone;
                          formData.supportEmail = document.getElementById('supportEmail')?.value || storeProfile.supportEmail;
                        }
                        alertConfig.onConfirm(formData);
                      }
                      setShowAlert(false);
                    }}
                  >
                    Save
                  </button>
                </>
              ) : alertConfig.type === 'warning' ? (
                <>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => setShowAlert(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => {
                      if (alertConfig.onConfirm) alertConfig.onConfirm();
                      setShowAlert(false);
                    }}
                  >
                    Yes, Remove
                  </button>
                </>
              ) : (
                <button 
                  className="btn btn-primary" 
                  onClick={() => setShowAlert(false)}
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Gallery Modal */}
      {showImageGallery && (
        <div className="sweet-alert-overlay" onClick={() => setShowImageGallery(false)}>
          <div className="gallery-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gallery-header">
              <h2>Select Store Logo</h2>
              <button className="close-btn" onClick={() => setShowImageGallery(false)}>×</button>
            </div>
            <div className="gallery-grid">
              {galleryImages.map((img, index) => (
                <div 
                  key={index} 
                  className="gallery-item"
                  onClick={() => handleImageSelect(img)}
                >
                  <img src={img} alt={`Logo ${index + 1}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Setting;